import _trimEnd from 'lodash-es/trimEnd';

import type { ThemeFontWeight } from '@rover/kibble/styles/themeTypes';
import { IconColorEnum } from '@rover/rsdk/src/apiClient/latest/model/iconColorEnum';
import { TextColorEnum } from '@rover/rsdk/src/apiClient/latest/model/textColorEnum';
import { isProdNodeEnv } from '@rover/rsdk/src/modules/Env/node';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

export function getNativeStyleFromArray(
  value: number | string | [string, string] | [string, string, string]
): string | number {
  if (!Array.isArray(value)) {
    return value;
  }
  return value[0];
}

export function pxToNumber(value: string | number): number {
  if (typeof value === 'number') return value;
  if (!value || !value.endsWith('px')) return 0;
  return parseInt(_trimEnd(value, 'px'), 10);
}

export type AvertaFontFamilyNames = 'Averta-Regular' | 'Averta-Semibold';
const convertFontWeight = (weight?: ThemeFontWeight): AvertaFontFamilyNames => {
  if (weight === 'semibold') {
    return 'Averta-Semibold';
  }
  return 'Averta-Regular';
};
export function fontWeightToFontFamily(
  weight?: ThemeFontWeight | ThemeFontWeight[]
): AvertaFontFamilyNames | AvertaFontFamilyNames[] {
  if (Array.isArray(weight)) {
    return weight.map(convertFontWeight);
  }
  return convertFontWeight(weight);
}
/**
 * Note: TextColorEnum and IconColorEnum are identical enums but exist as separate types
 * due to how they are generated from the API schema.
 * In the backend, both are mapped to the TextColor enum from the thin API.
 * This allows the getApiColor function to accept both types while maintaining type safety.
 */
export function getApiColor(color: TextColorEnum | IconColorEnum, theme: RoverTheme): string {
  if (!color) {
    Logger.warn(
      `getApiColor called with empty color: ${color}. Returning primary color as fallback.`
    );
    return theme.textColor.primary.toString();
  }
  switch (color) {
    case TextColorEnum.TEXT_COLOR_PRIMARY:
      return theme.textColor.primary.toString();
    case TextColorEnum.TEXT_COLOR_PRIMARY_INVERSE:
      return theme.textColor.primary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY:
      return theme.textColor.secondary.toString();
    case TextColorEnum.TEXT_COLOR_SECONDARY_INVERSE:
      return theme.textColor.secondary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY:
      return theme.textColor.tertiary.toString();
    case TextColorEnum.TEXT_COLOR_TERTIARY_INVERSE:
      return theme.textColor.tertiary_inverse.toString();
    case TextColorEnum.TEXT_COLOR_PLACEHOLDER:
      return theme.textColor.placeholder.toString();
    case TextColorEnum.TEXT_COLOR_DISABLED:
      return theme.textColor.disabled.toString();
    case TextColorEnum.TEXT_COLOR_PRICE:
      return theme.textColor.price.toString();
    case TextColorEnum.TEXT_COLOR_ERROR:
      return theme.textColor.error.toString();
    case TextColorEnum.TEXT_COLOR_CAUTION:
      return theme.textColor.caution.toString();
    case TextColorEnum.TEXT_COLOR_SUCCESS:
      return theme.textColor.success.toString();
    default:
      Logger.warn(`Unknown color: ${color}. Returning primary color as fallback.`);
      return theme.textColor.primary.toString();
  }
}

export function withOpacity(color: string, opacity: number): string {
  const percent = Math.round(opacity * 100);
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

const HEX_COLOR = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i;

/**
 * `withOpacity` for React Native, which cannot parse the `color-mix()` that the web
 * version emits. Takes a 6-digit hex token off the theme and returns `rgba()`, which
 * every platform understands.
 *
 * Prefer a semantic token that already carries the alpha you need (e.g.
 * `theme.background.overlay`). Reach for this only when no token matches the value.
 */
export function withOpacityNative(color: string, opacity: number): string {
  const match = HEX_COLOR.exec(color);

  if (!match) {
    Logger.warn(`withOpacityNative expects a 6-digit hex color, got: ${color}.`);
    return color;
  }

  const [, r, g, b] = match;
  return `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}, ${opacity})`;
}

/**
 * Reports a component-contract violation. In development it throws so misuse fails
 * loudly; in production it logs to Splunk via `Logger.warn` by default. Pass
 * `reportToSentry` for the rare violation that genuinely warrants Sentry alerting
 * (`Logger.error`) rather than a Splunk-only warning.
 */
export function throwOrLogError(
  errorText: string,
  { reportToSentry = false }: { reportToSentry?: boolean } = {}
): void {
  if (!isProdNodeEnv()) {
    throw new Error(errorText);
  }
  if (reportToSentry) {
    Logger.error(new Error(errorText));
  } else {
    // Preserve the stack so Splunk can still pinpoint the offending call site,
    // which is the whole reason these assertions exist.
    Logger.warn(errorText, { stack: new Error(errorText).stack ?? '' });
  }
}
